import PropTypes from 'prop-types';
import formatPrice from '@bwoty-web/price-formatter';
import { useState } from 'react';
import { Checkbox, Icon, RoomCard } from '@bwoty-web/ui-kit';
import { ICON_UI_CHECKMARK, UI_LOGO } from '@bwoty-web/ui-kit/icons';

import './cabinCard.scss';

const CabinCard = ({
  cabin,
  isGlobetrotter,
  labels,
  selectCabinOnClickHandler,
  selectedCabinKey,
  siteId,
  triggerReadMoreModal,
}) => {
  const { cabinName, description, fareTypePrices, images, key, totalPriceValue } = cabin;
  const [drinkPackageChecked, setDrinkPackageChecked] = useState(
    fareTypePrices?.find((fare) => fare?.key === selectedCabinKey)?.beverageIncluded || false,
  );

  const [largeImage] = images;
  const isSoldOut = !totalPriceValue;
  const fares = fareTypePrices ?? [];
  const baseFare = fares[0];
  const optionalDrinkFare = fares.length > 1 ? fares.find((fare) => fare.beverageIncluded) : undefined;
  const baseIncludesDrinkPackage = Boolean(baseFare?.beverageIncluded);
  const drinkPackageSelectedFare = drinkPackageChecked ? optionalDrinkFare : undefined;

  const selectCabinHandler = () => {
    if (isSoldOut) return;

    const fareKeyToSelect = drinkPackageChecked ? optionalDrinkFare?.key || key : key;
    selectCabinOnClickHandler(fareKeyToSelect);
  };

  const roomCardContent = {
    columnSpan: 4,
    description,
    externalImage:
      !largeImage?.cloudinaryUriTemplate && isGlobetrotter ? (
        <div className="iw-cabin-card__default-room-image">
          <Icon
            path={UI_LOGO}
            size="xl"
          />
        </div>
      ) : undefined,
    image: largeImage,
    ...(description && {
      linkList: [
        {
          'aria-haspopup': 'dialog',
          onClick: () => triggerReadMoreModal(cabin),
          text: labels.readMoreRoom,
        },
      ],
    }),
    onClick: selectCabinHandler,
    price: totalPriceValue,
    title: cabinName,
  };

  if (optionalDrinkFare) {
    const additionalCost = optionalDrinkFare.totalPriceValue - totalPriceValue;

    roomCardContent.additionalCardContent = (
      <div className="iw-cabin-card__drink-package-checkbox-container">
        <Checkbox
          uniqueId={key}
          checked={drinkPackageChecked}
          onChange={() => {
            const nextChecked = !drinkPackageChecked;
            setDrinkPackageChecked(nextChecked);
            if (key === selectedCabinKey || fareTypePrices?.some((fare) => fare.key === selectedCabinKey)) {
              selectCabinOnClickHandler(nextChecked ? optionalDrinkFare.key : key);
            }
          }}
        >
          {labels.addDrinkPackage}
          <span className="iw-cabin-card__drink-package-additional-price">
            &nbsp;+{formatPrice(additionalCost, siteId)}
          </span>
        </Checkbox>
      </div>
    );
  } else if (baseIncludesDrinkPackage) {
    roomCardContent.additionalCardContent = (
      <div className="iw-cabin-card__included-drink-package-container">
        <Icon
          path={ICON_UI_CHECKMARK}
          size="xs"
        />
        <p>{labels.drinkPackageIsIncludedInCabinPrice}</p>
      </div>
    );
  }

  roomCardContent.price =
    drinkPackageSelectedFare?.totalPriceValue ??
    (baseIncludesDrinkPackage ? baseFare?.totalPriceValue : totalPriceValue);

  if (optionalDrinkFare || baseIncludesDrinkPackage) {
    const showIncludedText = Boolean(drinkPackageSelectedFare || baseIncludesDrinkPackage);
    roomCardContent.footerPriceDescription = (
      <p className="iw-cabin-card__footer-price-description">
        {showIncludedText ? labels.drinkPackageIncluded : labels.drinkPackageExcluded}
      </p>
    );
  }

  if (isSoldOut) {
    roomCardContent.buttonText = labels.soldOut;
    roomCardContent.onClickElementProps = { disabled: true };
    roomCardContent.footerPriceDescription = null;
  }

  if (key === selectedCabinKey || fareTypePrices?.some((fare) => fare.key === selectedCabinKey)) {
    roomCardContent.buttonText = labels.roomSelectedBtnLabel;
    roomCardContent.buttonVariant = 'primary';
  }

  return <RoomCard {...roomCardContent} />;
};

CabinCard.propTypes = {
  cabin: PropTypes.shape().isRequired,
  isGlobetrotter: PropTypes.bool.isRequired,
  labels: PropTypes.shape().isRequired,
  selectCabinOnClickHandler: PropTypes.func.isRequired,
  selectedCabinKey: PropTypes.string.isRequired,
  siteId: PropTypes.number.isRequired,
  triggerReadMoreModal: PropTypes.func.isRequired,
};

export default CabinCard;
